import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { usePublicProjects } from '../hooks/usePublicProjects';
import Pagination from '../../../shared/components/Pagination';
import PublicLayout from './PublicLayout';

const peso = (value) => `₱${Number(value).toLocaleString()}`;
const PAGE_SIZE = 10;
const DISBURSED_STATUSES = new Set(['Ongoing', 'Completed']);

const statusStyles = {
  Planned: 'bg-gray-200 text-gray-600',
  Ongoing: 'bg-warning/10 text-warning-dark',
  Completed: 'bg-success/10 text-success',
};

function PublicProjectsPage() {
  const { projects, loading, error } = usePublicProjects();
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const categoryOptions = useMemo(
    () => [...new Set(projects.map((p) => p.category).filter(Boolean))].sort(),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return projects.filter((p) => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (!q) return true;
      return [p.name, p.category, p.voucher_number, p.recipient]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [projects, searchTerm, categoryFilter, statusFilter]);

  const totalDisbursed = useMemo(
    () =>
      filteredProjects
        .filter((p) => DISBURSED_STATUSES.has(p.status))
        .reduce((sum, p) => sum + Number(p.amount), 0),
    [filteredProjects]
  );

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProjects.slice(start, start + PAGE_SIZE);
  }, [filteredProjects, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  const hasActiveFilters = Boolean(searchTerm || categoryFilter || statusFilter);

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStatusFilter('');
  };

  return (
    <PublicLayout>
      <div className="w-full space-y-4">
        <section className="w-full bg-white rounded-lg border border-gray-200 p-4">
          <p className="-mx-4 -mt-4 mb-3 rounded-t-lg bg-navy-light px-4 py-2 text-md lg:text-lg font-semibold text-white tracking-wide">
            Projects &amp; Disbursements
          </p>

          <div className="flex items-center gap-2 w-full">
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, category, voucher #, recipient…"
                className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowFilters((prev) => !prev)}
              aria-label="Toggle filters"
              aria-pressed={showFilters}
              className={`shrink-0 p-2.5 rounded-md border transition ${
                showFilters
                  ? 'bg-navy-light text-white border-navy-light'
                  : 'border-gray-300 text-gray-500 hover:text-gray-900 hover:border-gray-400'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-center gap-2 w-full mt-2">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="">All Categories</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="">All Statuses</option>
                <option value="Planned">Planned</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Completed">Completed</option>
              </select>

              <button
                type="button"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="w-full sm:w-auto text-lg text-gray-500 border border-gray-300 px-3 py-2 rounded-md hover:text-gray-900 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-500"
              >
                Clear filters
              </button>
            </div>
          )}

          {!loading && !error && filteredProjects.length > 0 && (
            <div className="-mx-4 mt-3">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={filteredProjects.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          )}

          {loading ? (
            <p className="py-8 text-sm text-gray-500 text-center">Loading projects…</p>
          ) : error ? (
            <p className="py-8 text-sm text-danger text-center">{error}</p>
          ) : filteredProjects.length === 0 ? (
            <p className="py-8 text-sm text-gray-500 text-center">
              {projects.length === 0 ? 'No projects recorded yet' : 'No projects match your search/filters'}
            </p>
          ) : (
            <>
              <div className="hidden sm:block -mx-4 overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-md text-navy uppercase">
                      <th className="py-2 pl-4 pr-4 font-semibold whitespace-nowrap">Name</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Category</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Amount</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Date</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Voucher/OR #</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Recipient</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedProjects.map((project) => (
                      <tr key={project.id} className="hover:bg-gold/10 active:bg-gold/20 transition-colors">
                        <td className="py-3 pl-4 pr-4 font-medium text-gray-900 whitespace-nowrap">{project.name}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.category || '—'}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(project.amount)}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.expense_date}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.voucher_number || '—'}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.recipient || '—'}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              statusStyles[project.status] ?? 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {project.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sm:hidden space-y-3 mt-3">
                {paginatedProjects.map((project) => (
                  <div
                    key={project.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gold/10 active:bg-gold/20 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-gray-900">{project.name}</p>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          statusStyles[project.status] ?? 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {project.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-1 text-sm">
                      <p className="text-gray-500">Category</p>
                      <p className="text-right font-bold text-gray-700">{project.category || '—'}</p>
                      <p className="text-gray-500">Amount</p>
                      <p className="text-right font-bold text-gray-700">{peso(project.amount)}</p>
                      <p className="text-gray-500">Date</p>
                      <p className="text-right font-bold text-gray-700">{project.expense_date}</p>
                      <p className="text-gray-500">Voucher/OR #</p>
                      <p className="text-right font-bold text-gray-700">{project.voucher_number || '—'}</p>
                      <p className="text-gray-500">Recipient</p>
                      <p className="text-right font-bold text-gray-700">{project.recipient || '—'}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mt-4 px-4 py-3 rounded-lg text-xl sm:text-2xl font-bold bg-danger/10 text-danger">
                <span>Total Disbursed</span>
                <span>{peso(totalDisbursed)}</span>
              </div>
            </>
          )}
        </section>
      </div>
    </PublicLayout>
  );
}

export default PublicProjectsPage;
